import { useMemo, useState } from 'react';
import { ActivityIndicator, TextInput, View } from 'react-native';

import { LlmModelCatalogCard } from '@/components/settings/LlmModelCatalogCard.web';
import type { LlmProfileChoice } from '@/components/settings/LlmControlProfiles.web';
import { Badge, Button, Text } from '@/components/ui';
import { refreshAdminLlmCatalog, type OpenRouterCatalogMetadata } from '@/lib/admin/llm-models-api';
import {
  formatContextTokens,
  formatCount,
  formatPricePer1M,
  retentionSummary,
  type AdminLlmModel,
  type AdminLlmProfile,
  type LlmCatalogCandidate,
  type LlmCatalogResponse,
  type LlmCatalogSource,
  type LlmProfileId,
} from '@/lib/admin/llm-models-types';

const PROFILE_LABEL: Record<LlmProfileId, string> = {
  fast: 'Fast',
  balanced: 'Balanced',
  capable: 'Capable',
};

type LlmControlCatalogueProps = {
  models: AdminLlmModel[];
  profiles: AdminLlmProfile[];
  catalog: LlmCatalogResponse | null;
  catalogLoading: boolean;
  usingKey: string | null;
  onCatalog: (catalog: LlmCatalogResponse) => void;
  onUse: (choice: LlmProfileChoice) => void;
  onProviderTag: (model: AdminLlmModel, tag: string | null) => void;
  onError: (message: string | null) => void;
  openrouterModelId: string;
  displayName: string;
  lookupLoading: boolean;
  lookupError: string | null;
  lookupPreview: OpenRouterCatalogMetadata | null;
  saving: boolean;
  onOpenrouterModelId: (value: string) => void;
  onDisplayName: (value: string) => void;
  onAddModel: () => void;
  onToggleEnabled: (model: AdminLlmModel) => void;
  onRefreshMetadata: (model: AdminLlmModel) => void;
  onRename: (model: AdminLlmModel) => void;
  onDelete: (model: AdminLlmModel) => void;
};

function sourceLabel(source: LlmCatalogSource): string {
  if (source.kind === 'usage') return `Usage #${source.rank}`;
  const task =
    source.task === 'coding' ? 'Coding' : source.task === 'agentic' ? 'Agentic' : 'Intelligence';
  return `${task} #${source.rank}`;
}

function statLine(candidate: LlmCatalogCandidate): string {
  const latency =
    candidate.latencyP50Sec == null ? null : `${Math.round(candidate.latencyP50Sec * 1000)} ms`;
  return [
    candidate.throughputP50 == null ? null : `${formatCount(candidate.throughputP50)} tok/s`,
    latency,
    `${formatPricePer1M(candidate.promptPer1M)} / ${formatPricePer1M(candidate.completionPer1M)} per 1M`,
    formatContextTokens(candidate.contextLength),
  ]
    .filter(Boolean)
    .join(' · ');
}

export function LlmControlCatalogue(props: LlmControlCatalogueProps) {
  const [query, setQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rows = props.catalog?.candidates ?? [];
    if (!needle) return rows;
    return rows.filter((row) => `${row.name} ${row.modelId}`.toLowerCase().includes(needle));
  }, [props.catalog, query]);

  const assigned = new Map(
    props.profiles
      .filter((profile) => profile.model)
      .map((profile) => [profile.model!.openrouterModelId, profile.profile]),
  );

  async function onRefresh() {
    setRefreshing(true);
    props.onError(null);
    try {
      props.onCatalog(await refreshAdminLlmCatalog());
    } catch (err) {
      props.onError(err instanceof Error ? err.message : 'Failed to refresh catalogue');
    } finally {
      setRefreshing(false);
    }
  }

  function useCandidate(profile: LlmProfileId, candidate: LlmCatalogCandidate) {
    props.onUse({
      profile,
      modelId: candidate.modelId,
      name: candidate.displayName ?? candidate.name,
      allowlistId: candidate.allowlistId,
      enabled: candidate.enabled,
      privacy: candidate.privacy,
    });
  }

  return (
    <View className="mt-6 flex flex-col gap-6">
      <View className="rounded-lg border border-border p-4">
        <View className="flex-row items-start justify-between gap-3">
          <View className="min-w-0 flex-1">
            <Text variant="h3">Top lists</Text>
            <Text variant="caption" className="mt-1 text-muted">
              {props.catalog?.fetchedAt
                ? `Refreshed ${new Date(props.catalog.fetchedAt).toLocaleString()}. These are OpenRouter benchmark and usage leaders that have a zero-retention route. Use one to assign a profile.`
                : 'Refresh to load OpenRouter benchmark and usage leaders.'}
            </Text>
          </View>
          <View className="shrink-0">
            <Button
              label={refreshing ? 'Refreshing…' : 'Refresh'}
              loading={refreshing}
              size="sm"
              variant="primary"
              onPress={() => void onRefresh()}
            />
          </View>
        </View>
        {props.catalogLoading ? <ActivityIndicator className="mt-4" /> : null}
        {props.catalog
          ? (Object.keys(PROFILE_LABEL) as LlmProfileId[]).map((profile) => (
              <View key={profile} className="mt-4">
                <Text variant="body" className="font-medium">
                  Best for {PROFILE_LABEL[profile]}
                </Text>
                {props.catalog?.suggestions[profile].length ? (
                  props.catalog.suggestions[profile].map((suggestion) => {
                    const candidate = props.catalog?.candidates.find(
                      (row) => row.modelId === suggestion.modelId,
                    );
                    const current = assigned.get(suggestion.modelId) === profile;
                    const busy = props.usingKey === `${profile}:${suggestion.modelId}`;
                    return (
                      <View
                        key={suggestion.modelId}
                        className="mt-2 rounded border border-border p-3"
                      >
                        <Text variant="body-sm">
                          {suggestion.displayName ?? candidate?.name ?? suggestion.modelId}
                        </Text>
                        <Text variant="caption" className="text-muted">
                          {suggestion.modelId}
                        </Text>
                        <Text variant="caption" className="mt-1 text-muted">
                          {candidate
                            ? retentionSummary(candidate.privacy)
                            : 'Retention not loaded.'}
                        </Text>
                        {candidate ? (
                          <Text variant="caption" className="mt-1 text-muted">
                            {statLine(candidate)}
                          </Text>
                        ) : null}
                        <View className="mt-2 self-start">
                          <Button
                            label={
                              current
                                ? `Current ${PROFILE_LABEL[profile]}`
                                : busy
                                  ? 'Assigning…'
                                  : `Use for ${PROFILE_LABEL[profile]}`
                            }
                            disabled={current || !candidate}
                            loading={busy}
                            size="sm"
                            variant={current ? 'secondary' : 'primary'}
                            onPress={() => {
                              if (candidate) useCandidate(profile, candidate);
                            }}
                          />
                        </View>
                      </View>
                    );
                  })
                ) : (
                  <Text variant="caption" className="mt-1 text-muted">
                    No suggestion yet.
                  </Text>
                )}
              </View>
            ))
          : null}
      </View>

      <View className="rounded-lg border border-border p-4">
        <Text variant="h3">Shortlist</Text>
        <Text variant="caption" className="mt-1 text-muted">
          Search the same shortlist and assign any of these models to a profile.
        </Text>
        <TextInput
          autoCapitalize="none"
          className="mb-3 mt-3 w-full rounded-lg border border-border px-3 py-2 text-base"
          placeholder="Search this shortlist"
          value={query}
          onChangeText={setQuery}
        />
        {filtered.length === 0 ? (
          <Text variant="caption" className="text-muted">
            {props.catalog
              ? 'Nothing in this shortlist matches.'
              : 'Refresh the catalogue to load a shortlist.'}
          </Text>
        ) : (
          filtered.map((candidate) => (
            <View key={candidate.modelId} className="mt-3 border-t border-border pt-3">
              <Text variant="body">{candidate.displayName ?? candidate.name}</Text>
              <Text variant="caption" className="text-muted">
                {candidate.modelId}
              </Text>
              <View className="mt-1 flex-row flex-wrap gap-1">
                {candidate.sources.map((source) => (
                  <Badge
                    key={`${source.kind}-${source.task ?? 'usage'}-${source.rank}`}
                    label={sourceLabel(source)}
                    variant="neutral"
                  />
                ))}
                {candidate.hasZdr ? (
                  <Badge label="Zero retention" variant="success" />
                ) : (
                  <Badge label="No zero-retention route" variant="danger" />
                )}
              </View>
              <Text variant="caption" className="mt-1 text-muted">
                {retentionSummary(candidate.privacy)}
              </Text>
              <Text variant="caption" className="mt-1 text-muted">
                {statLine(candidate)}
              </Text>
              <View className="mt-2 flex-row flex-wrap gap-2">
                {(Object.keys(PROFILE_LABEL) as LlmProfileId[]).map((profile) => {
                  const current = assigned.get(candidate.modelId) === profile;
                  const busy = props.usingKey === `${profile}:${candidate.modelId}`;
                  return (
                    <Button
                      key={profile}
                      label={
                        current ? `${PROFILE_LABEL[profile]} · current` : PROFILE_LABEL[profile]
                      }
                      disabled={current}
                      loading={busy}
                      size="sm"
                      variant={current ? 'primary' : 'secondary'}
                      onPress={() => useCandidate(profile, candidate)}
                    />
                  );
                })}
              </View>
            </View>
          ))
        )}
      </View>

      <View className="rounded-lg border border-border p-4">
        <Text variant="h3" className="mb-1">
          Add by id
        </Text>
        <Text variant="caption" className="mb-3 text-muted">
          For a model that is not on the shortlist. It still has to offer a zero-retention route.
        </Text>
        <TextInput
          autoCapitalize="none"
          className="mb-2 w-full rounded-lg border border-border px-3 py-2 text-base"
          placeholder="OpenRouter model id (e.g. anthropic/claude-sonnet-4)"
          value={props.openrouterModelId}
          onChangeText={props.onOpenrouterModelId}
        />
        <TextInput
          autoCapitalize="words"
          className="mb-3 w-full rounded-lg border border-border px-3 py-2 text-base"
          placeholder="Display name (optional, filled from catalog)"
          value={props.displayName}
          onChangeText={props.onDisplayName}
        />
        {props.lookupLoading ? (
          <ActivityIndicator className="mb-3" />
        ) : props.lookupError ? (
          <Text variant="caption" className="mb-3 text-danger">
            {props.lookupError}
          </Text>
        ) : props.lookupPreview ? (
          <View className="mb-3">
            <LlmModelCatalogCard metadata={props.lookupPreview} />
          </View>
        ) : props.openrouterModelId.trim() ? (
          <Text variant="caption" className="mb-3 text-muted">
            Enter a full OpenRouter id (author/model) to preview retention and routes.
          </Text>
        ) : null}
        <Button
          label="Add model"
          loading={props.saving}
          disabled={
            !props.openrouterModelId.trim().includes('/') ||
            props.lookupPreview == null ||
            props.lookupPreview.privacy.securityTier === 'unsafe'
          }
          size="sm"
          variant="primary"
          onPress={props.onAddModel}
        />
      </View>

      <View className="flex flex-col gap-4">
        <Text variant="h3">Registered models</Text>
        <Text variant="body-sm" className="text-muted">
          Pick the privacy route for each model. Calls use that route with data collection denied.
        </Text>
        {props.models.length === 0 ? (
          <Text variant="body" className="text-muted">
            No models configured yet.
          </Text>
        ) : (
          props.models.map((model) => (
            <View key={model.id} className="rounded-lg border border-border p-3">
              <View className="flex-row items-start justify-between gap-3">
                <View className="min-w-0 flex-1">
                  <Text variant="body" className="font-medium">
                    {model.displayName}
                  </Text>
                  <Text variant="caption" className="mt-0.5 text-muted">
                    {model.openrouterModelId}
                  </Text>
                </View>
                <Badge
                  label={model.enabled ? 'Enabled' : 'Disabled'}
                  variant={model.enabled ? 'success' : 'neutral'}
                />
              </View>
              <View className="mt-2 flex-row flex-wrap gap-3">
                <Button
                  label={model.enabled ? 'Disable' : 'Enable'}
                  size="sm"
                  disabled={
                    !model.enabled && model.openrouterMetadata?.privacy.securityTier === 'unsafe'
                  }
                  onPress={() => props.onToggleEnabled(model)}
                />
                <Button
                  label="Refresh metadata"
                  size="sm"
                  onPress={() => props.onRefreshMetadata(model)}
                />
                <Button label="Rename" size="sm" onPress={() => props.onRename(model)} />
                <Button
                  label="Delete"
                  size="sm"
                  variant="danger"
                  onPress={() => props.onDelete(model)}
                />
              </View>
              {model.openrouterMetadata ? (
                <View className="mt-3">
                  <LlmModelCatalogCard
                    metadata={model.openrouterMetadata}
                    providerTag={model.providerTag}
                    onProviderTag={(tag) => props.onProviderTag(model, tag)}
                    compact
                  />
                </View>
              ) : (
                <Text variant="caption" className="mt-3 text-muted">
                  No cached metadata. Refresh metadata to see retention and choose a route.
                </Text>
              )}
            </View>
          ))
        )}
      </View>
    </View>
  );
}
